import { CreateResourceParams, queryKeys } from "./ReactQuery/reactQueryService";
import { apiPatch, apiPost } from "./Api/apiCall";
import { QueryClient } from "@tanstack/react-query";
import { Dispatch, SetStateAction } from "react";
import { ProductUnion } from "../types/ProductUnion";
import { BackofficeUser, Roles } from "../types/BackofficeUser";
import { userHasOneOfTheseRoles } from "./backofficeUserService";
import { AiChatAgent, AiChatThread } from "../types/AIChat/AiChatThread";
import { AiChatVoiceTranscription } from "../types/AIChat/AiChatVoiceTranscription";
import { AiChatMessage } from "../types/AIChat/AiChatMessage";
import { AiEventStreamStore } from "../store/AiEventStreamStore";

export const transcribeAiChatVoiceRecording = (audio: Blob): Promise<AiChatVoiceTranscription> => {
  const formData = new FormData();
  formData.append("audio", audio, "voice-recording.webm");

  return apiPost("ai_chat/voice_transcriptions", formData);
};

type SendAiChatPromptParams = {
  product: ProductUnion;
  activeThreadId: number | null;
  setActiveThreadId: Dispatch<SetStateAction<number | null>>;
  createMutation: { mutateAsync: (params: CreateResourceParams) => Promise<AiChatThread> };
  queryClient: QueryClient;
  getStreamStore: (threadId: number) => AiEventStreamStore;
  prompt: string;
  setIsCreatingThread: Dispatch<SetStateAction<boolean>>;
  agentType: AiChatAgent;
  activeExecutionStepId?: number | null;
  editedMessageUuid?: string | null;
};

export const refreshAiChatThread = async (queryClient: QueryClient, threadId: number): Promise<void> => {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: queryKeys.collection("ai_chat_messages", { aiChatThread: threadId }),
    }),
    queryClient.invalidateQueries({ queryKey: queryKeys.item("ai_chat_threads", threadId) }),
  ]);
};

export const sendAiChatPrompt = async ({
  product,
  activeThreadId,
  setActiveThreadId,
  createMutation,
  queryClient,
  getStreamStore,
  prompt,
  setIsCreatingThread,
  agentType,
  activeExecutionStepId = null,
  editedMessageUuid = null,
}: SendAiChatPromptParams) => {
  const sendToThread = async (threadId: number) => {
    const store = getStreamStore(threadId);
    if (store.getCurrentStreamId() !== null && store.getSnapshot().status !== "error") {
      throw new Error("An AI response is already running for this thread.");
    }

    let streamId: string | null = null;
    const subscriptions: (() => void)[] = [];
    const unsubscribe = () => subscriptions.splice(0).forEach((stop) => stop());

    try {
      streamId = store.connect();
      let completion: Promise<void> | null = null;
      const finish = (resetStream = true): Promise<void> => {
        completion ??= refreshAiChatThread(queryClient, threadId).finally(() => {
          unsubscribe();
          if (resetStream && store.getCurrentStreamId() === streamId) {
            store.reset();
          }
        });
        return completion;
      };

      // These listeners belong to the submitted run, not to the currently displayed chat.
      subscriptions.push(
        store.addEventListener("threadUpdated", () => refreshAiChatThread(queryClient, threadId)),
        store.addEventListener("streamClosed", () => finish()),
        store.addEventListener("streamCancelled", () => finish()),
        store.addEventListener("streamError", () => finish(false)),
        store.subscribe(() => {
          if (store.getCurrentStreamId() !== streamId) {
            unsubscribe();
          }
        }),
      );

      await apiPost("ai_chat_messages", {
        content: prompt,
        aiChatThread: "/ai_chat_threads/" + threadId,
        streamId,
        ...(activeExecutionStepId !== null && { activeExecutionStepId, mode: "execution_step_edit" }),
        ...(editedMessageUuid !== null && { editedMessageUuid }),
      });
      // The synchronous API response also confirms persistence if the final SSE event was missed.
      await finish();
    } catch (error) {
      unsubscribe();
      if (streamId === null || store.getCurrentStreamId() === streamId) {
        store.reset();
      }
      throw error;
    }
  };

  if (activeThreadId !== null) {
    return sendToThread(activeThreadId);
  }

  setIsCreatingThread(true);
  try {
    const thread = await createMutation.mutateAsync({
      uri: "ai_chat_threads",
      data: {
        backofficeCase: product.backofficeCase["@id"],
        agent: agentType,
        title: prompt,
      },
    });
    const sending = sendToThread(thread.id);
    setActiveThreadId((currentThreadId) => currentThreadId === null ? thread.id : currentThreadId);
    return Promise.all([
      sending,
      queryClient.invalidateQueries({
        queryKey: queryKeys.collection("ai_chat_threads", { backofficeCase: product.backofficeCase["@id"] }),
      }),
    ]).then(() => undefined);
  } finally {
    setIsCreatingThread(false);
  }
};

export const selectAiChatBranch = async (
  threadId: number,
  currentLeafMessageUuid: string,
): Promise<void> => {
  await apiPatch("ai_chat_threads", threadId, { currentLeafMessageUuid });
};

// Returns the conversation as currently displayed: walks from the thread's selected leaf
// up the parent links to the root and returns that path in top-to-bottom order. Messages
// on other branches are left out; an unknown leaf or a broken chain yields an empty array.
export const getAiChatThreadPath = (
  messages: AiChatMessage[],
  currentLeafMessageUuid: string | null,
): AiChatMessage[] => {
  const messagesByUuid = new Map(messages.map((message) => [message.uuid, message]));
  const path: AiChatMessage[] = [];
  let currentMessage = currentLeafMessageUuid === null ? null : messagesByUuid.get(currentLeafMessageUuid) ?? null;

  while (currentMessage !== null) {
    path.unshift(currentMessage);

    if (currentMessage.parentMessageUuid === null) {
      return path;
    }
    currentMessage = messagesByUuid.get(currentMessage.parentMessageUuid) ?? null;
  }

  return [];
};

// Returns all variants of a message, i.e. its siblings in the message tree (same parent,
// including the message itself). These are the branches the arrow buttons switch between.
export const getAiChatMessageVariants = (
  messages: AiChatMessage[],
  message: AiChatMessage,
): AiChatMessage[] => messages.filter((candidate) => candidate.parentMessageUuid === message.parentMessageUuid);

export const getLatestAiChatLeafUuid = (messages: AiChatMessage[], rootUuid: string): string => {
  // Messages arrive in creation order and children are always created after their
  // parent, so one pass collects the subtree and its most recent leaf.
  const subtreeUuids = new Set([rootUuid]);
  const parentUuidsInSubtree = new Set<string>();
  for (const message of messages) {
    if (message.parentMessageUuid !== null && subtreeUuids.has(message.parentMessageUuid)) {
      subtreeUuids.add(message.uuid);
      parentUuidsInSubtree.add(message.parentMessageUuid);
    }
  }

  let latestLeafUuid = rootUuid;
  for (const uuid of subtreeUuids) {
    if (!parentUuidsInSubtree.has(uuid)) {
      latestLeafUuid = uuid;
    }
  }

  return latestLeafUuid;
};

export const cancelAiChatWorkflow = async (threadId: number): Promise<void> => {
  await apiPost(`ai_chat_threads/${threadId}/cancel`);
};

export const getAiAgentByUser = (user: BackofficeUser) => {
  switch (true) {
    case userHasOneOfTheseRoles(user, [
      Roles.admin,
      Roles.traineeLawyer,
      Roles.lawyer,
      Roles.leadLawyer,
      Roles.developer,
      Roles.productManager,
      Roles.banking,
    ]):
      return "LEXA";
    case userHasOneOfTheseRoles(user, [
      Roles.accountmanager,
      Roles.leadAccountmanager,
      Roles.basicAccounting,
      Roles.accounting,
    ]):
      return "LIA";
    default:
      return null;
  }
};
